import { forwardRef } from 'react';
import { Tourbook } from '@root/types';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Image, Transformation } from 'cloudinary-react';
import useEnv from '@shared/useEnv';
import classnames from 'classnames';
import Button from '@components/shared/V2Button';
import EditTourbookModal from '@components/shared/EditTourbookModal';
import ClientInformation from './ClientInformation';
import ContactInformation from './ContactInformation';
import tourbookStyles from './Header.module.less';

type Props = {
  tourbook: Tourbook;
  readOnly: boolean;
  onEditClick: () => void;
  onCloseModal: () => void;
  isModalOpen: boolean;
  hidden: boolean;
};
const Header = forwardRef<HTMLDivElement | null, Props>(
  ({ tourbook, readOnly, onEditClick, onCloseModal, isModalOpen, hidden }: Props, ref) => {
    /*
  TODO:
    - test this component
    - figure out if we can nullify the file input when the image is removed?
    - this is a first pass to get to demo-able state
   */
    const flags = useFlags();
    const { t } = useTranslation('tourbook');
    const { cloudinaryCdn, cloudinaryCloud } = useEnv();
    const srcSetFor = photo =>
      `${photo.largePath} 1500w, ${photo.largePath} 900w, ${photo.mediumPath} 400w`;
    const containsDescriptionBodyElements = tbook =>
      tbook.name && (tbook.description || tbook.clientName || tbook.clientLogoImage);

    const renderHeaderImage = () => {
      if (flags['tourbook-header-image-refactor']) {
        return (
          <Image
            className={tourbookStyles.headerImage}
            cloudName={cloudinaryCloud}
            privateCdn={cloudinaryCdn}
            publicId={tourbook.headerImage.cloudinaryId}
          >
            <Transformation
              crop="fill"
              fetchFormat="jpg"
              quality="100"
              width="1500"
              height="1500"
              gravity="auto"
            />
          </Image>
        );
      }
      if (tourbook.headerImage.largePath) {
        return (
          <img
            className={tourbookStyles.headerImage}
            src={tourbook.headerImage.largePath}
            alt={tourbook.headerImage.description}
            srcSet={srcSetFor(tourbook.headerImage)}
          />
        );
      }
      return null;
    };

    return (
      <div id="tourbook-header" className={tourbookStyles.headerContainer} ref={ref}>
        <header
          className={classnames([
            tourbookStyles.header,
            tourbook.headerImage && tourbookStyles.withHeaderImage,
            hidden && tourbookStyles.hidden,
          ])}
        >
          <div className={tourbookStyles.headerImage}>
            {tourbook.headerImage ? (
              renderHeaderImage()
            ) : (
              <>
                <div className={tourbookStyles.uploadHeaderImageContainer}>
                  {!readOnly && (
                    <Button
                      type="quarternary"
                      onClick={onEditClick}
                      className={tourbookStyles.uploadHeaderImage}
                    >
                      {t('header.uploadHeaderImage')}
                    </Button>
                  )}
                </div>
                <img
                  className={tourbookStyles.headerImagePlaceholder}
                  src="/images/tourbook-header-default.jpg"
                  alt="Tourbook Header"
                />
              </>
            )}
          </div>
          <div className={tourbookStyles.descriptionSection}>
            <div className={tourbookStyles.descriptionBody}>
              <div
                className={classnames([
                  tourbookStyles.descriptionBody,
                  containsDescriptionBodyElements(tourbook) &&
                    tourbookStyles.withDescriptionContent,
                ])}
              >
                <div>
                  <h4 className={tourbookStyles.descriptionTitle}>{tourbook.name}</h4>
                  {tourbook.description && (
                    <span className={tourbookStyles.description}>{tourbook.description}</span>
                  )}
                </div>
                <ClientInformation name={tourbook.clientName} image={tourbook.clientLogoImage} />
              </div>
            </div>
          </div>

          <section className={tourbookStyles.contactSection}>
            {tourbook && <ContactInformation user={tourbook.owner} readOnly={readOnly} />}
          </section>
          <EditTourbookModal
            tourbook={tourbook}
            isModalOpen={isModalOpen}
            onCloseModal={onCloseModal}
          />
        </header>
      </div>
    );
  },
);

export default Header;
